import { ApolloClient, useApolloClient, useMutation, makeVar, useReactiveVar } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// Data
import { store } from '../utils/storage';
import { SessionReporter } from '../utils/session.reporter';

// Queries
import { LOGOUT } from '../queries/auth.queries';
import { checkHasTrackedQueries } from '../utils/tracker.link';
import { useOnlineStatus } from '../stores/connectivity.store';
import { recordUserSession } from '../utils/user-session.service';
import { clearPromptCooldownsForUser } from '../helpers/prompt-scheduler.helpers';
import { clearLastActivityAt, writeLastActivityAt } from '../helpers/idle-activity.helpers';
import { HOME_ROUTE, PARENT_HOME_ROUTE, PARENT_LOGIN_ROUTE } from '../../../_configuration';
import { SessionType } from '../types/session.types';

// Staff and parent are entirely separate sessions held side by side: each has
// its own persisted token, and which one is "active" is derived from the URL.
export const STAFF_TOKEN_KEY = 'mea_token';
export const PARENT_TOKEN_KEY = 'mea_parent_token';

export const tokenKeyForTree = (tree: SessionType) =>
  (tree === SessionType.PARENT ? PARENT_TOKEN_KEY : STAFF_TOKEN_KEY);

// Which session tree a path belongs to. Used to pick the user var (getAuthUser)
// and to scope cross-tab logout; token selection is per-client, not URL-derived.
export const treeFromPath = (pathname: string): SessionType =>
  (pathname === PARENT_HOME_ROUTE || pathname.startsWith(`${PARENT_HOME_ROUTE}/`) ? SessionType.PARENT : SessionType.STAFF);

export const staffUser = makeVar<IUser | null>(null);
export const parentUser = makeVar<IUser | null>(null);

const userVarForTree = (tree: SessionType) => (tree === SessionType.PARENT ? parentUser : staffUser);

// HOOK: returns the authenticated user for the tree the current URL belongs to (reads
// window.location, not useLocation, so consumers mounted outside the Router work too).
export const getAuthUser = () => {
  const tree = treeFromPath(window.location.pathname);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useReactiveVar(tree === SessionType.PARENT ? parentUser : staffUser);
};

export const setAuthenticatedUser = (user: any, tree: SessionType = SessionType.STAFF, { identify = true } = {}) => {
  userVarForTree(tree)(user);
  if (user && identify) {
    SessionReporter.identify({
      _id: user._id,
      name: user.name,
      email: user.email
    });
  }
};

type SettingsUpdate = Record<string, unknown> | ((settings: Record<string, any>) => Record<string, unknown>);

// Merges settings into the signed-in user as it is now, never a caller's older copy; other settings stay as they are.
// Pass a function to derive the update from the current settings.
export const setAuthUserSettings = (update: SettingsUpdate) => {
  const user = staffUser();
  if (!user) return;
  const settings = typeof update === 'function' ? update(user.settings || {}) : update;
  setAuthenticatedUser({ ...user, settings: { ...user.settings, ...settings } });
};

export const buildSetAuthUser = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const client = useApolloClient();

  return (user: any, tree: SessionType, redirect?: string) =>
    // Seed the local user-session buffer for both trees (staff scores+edits, parent player
    // edits); the network-side INITIALIZE_USER_SESSION call stays staff-only.
    recordUserSession(user)
      .then(async () => {
        // This app's cache is isolated, so clearing it on login wipes only the
        // previous user's data on a shared device — the sibling app is untouched.
        await client.clearStore();
        await store.set(tokenKeyForTree(tree), user.token);
        // A fresh sign-in starts a fresh idle window; a stamp left by an older session would expire it at once.
        if (tree === SessionType.STAFF) writeLastActivityAt(user._id, Date.now());
        userVarForTree(tree)(user);
      })
      .then(() => {
        SessionReporter.identify({
          _id: user._id,
          name: user.name,
          email: user.email
        });

        if (redirect) {
          window.location.href = redirect;
        }
      });
};

// Broadcast logout to same-origin tabs via localStorage (only it fires cross-tab `storage`
// events); the timestamp guarantees a value change so repeat sign-outs re-fire.
const LOGOUT_KEY = 'logout';

export const broadcastLogout = (tree: SessionType) => {
  try {
    localStorage.setItem(LOGOUT_KEY, `${tree}:${Date.now()}`);
  } catch (err) {
    // Best-effort cross-tab signal; storage being unavailable (private mode,
    // quota) must not abort the sign-out flow that calls this.
    console.error('broadcastLogout failed to write logout signal:', err);
  }
};

// Clears one tree's local session: user var, token, cache, and same-tree tabs. Not a
// hook, so the session-expiry link can end a session the server already rejected.
export const teardownSession = async (tree: SessionType, client: ApolloClient<unknown>) => {
  // Scope the cooldown reset to the departing user so a sign-out never wipes the sibling
  // tree's still-signed-in user's cooldowns — the two trees are independent sessions.
  const signingOutUserId = userVarForTree(tree)()?._id;
  userVarForTree(tree)(null);
  if (signingOutUserId) clearPromptCooldownsForUser(signingOutUserId);
  await store.remove(tokenKeyForTree(tree));
  // Only once the token is gone: a stamp cleared ahead of a failed removal restarts the idle window on reload.
  if (signingOutUserId && tree === SessionType.STAFF) clearLastActivityAt(signingOutUserId);

  // `client` is this app's isolated client (from its own provider), so clearing
  // its store vacates only this session's data — the sibling app is untouched.
  SessionReporter.logout();
  await client.clearStore();

  // Broadcast to same-tree tabs only (value carries the tree); a parent
  // sign-out must not bounce a staff tab and vice versa.
  broadcastLogout(tree);
};

export const buildSignOut = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const history = useHistory();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const client = useApolloClient();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [logout] = useMutation(LOGOUT);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isOnline = useOnlineStatus();

  return async (tree: SessionType, redirect?: boolean) => {
    // Only the staff app buffers offline scoring data, so the unsaved-data guard
    // applies there alone; the parent app has nothing queued to lose.
    if (tree === SessionType.STAFF) {
      const hasUnsavedData = await checkHasTrackedQueries();

      if (hasUnsavedData && isOnline) Sentry.captureException('User is attempting to sign out with data in queue.');
      if (hasUnsavedData && !isOnline && !window.confirm('You have unsaved scores. These will be lost if you sign out before connecting to stable internet. Are you sure you want to sign out?')) {
        return Sentry.captureMessage(`Unable to log the user out (unsaved: ${hasUnsavedData}, online: ${isOnline}`);
      }
    }

    try {
      await logout();
    } catch (err) {
      // The server logout is best-effort: always tear down the local session so
      // the user isn't stranded signed-in when offline or the call fails.
      console.error('buildSignOut logout mutation failed:', err);
    }

    await teardownSession(tree, client);

    if (redirect) history.push(tree === SessionType.PARENT ? PARENT_LOGIN_ROUTE : HOME_ROUTE);
  };
};

function syncLogout(event: StorageEvent) {
  if (event.key === LOGOUT_KEY && event.newValue) {
    const broadcastTree: SessionType = event.newValue.startsWith(SessionType.PARENT) ? SessionType.PARENT : SessionType.STAFF;
    if (treeFromPath(window.location.pathname) === broadcastTree) {
      const signingOutUserId = userVarForTree(broadcastTree)()?._id;
      if (signingOutUserId) clearPromptCooldownsForUser(signingOutUserId);
      window.location.href = broadcastTree === SessionType.PARENT ? PARENT_LOGIN_ROUTE : HOME_ROUTE;
    }
  }
}

window.removeEventListener('storage', syncLogout);
window.addEventListener('storage', syncLogout);
